import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, CircularProgress,
  InputAdornment, FormControlLabel, Checkbox, Radio, RadioGroup,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import LabelIcon from '@mui/icons-material/Label';
import Select from 'react-select';

import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  accentDim: 'rgba(45,212,191,0.10)', accentBorder: 'rgba(45,212,191,0.22)',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444',
};

// ── react-select styles ───────────────────────────────────────────────────────
const rsStyles = {
  control: (base, state) => ({
    ...base, minHeight: '30px', height: '30px', fontSize: '12px',
    borderRadius: '7px', backgroundColor: '#fafafa',
    borderColor: state.isFocused ? C.accent : C.cardBorder,
    boxShadow: state.isFocused ? `0 0 0 1.5px ${C.accent}` : 'none',
    '&:hover': { borderColor: C.accentBorder },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  input: (base) => ({ ...base, fontSize: '12px', margin: 0, padding: 0 }),
  singleValue: (base) => ({ ...base, fontSize: '12px' }),
  option: (base, state) => ({
    ...base, fontSize: '12px',
    backgroundColor: state.isSelected ? C.accent : state.isFocused ? C.accentDim : '#fff',
    color: state.isSelected ? '#fff' : C.text,
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// ── Reusable field components ─────────────────────────────────────────────────
const Field = ({ label, name, value, onChange, type = 'text', required, align, maxLength, disabled, placeholder }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
    <Typography sx={{ fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}{required && <span style={{ color: C.danger, marginLeft: 2 }}>*</span>}
    </Typography>
    <TextField
      size="small" name={name} value={value ?? ''} onChange={onChange}
      placeholder={placeholder || label} type={type} disabled={disabled}
      inputProps={{ maxLength, style: { fontSize: '12px', textAlign: align || 'left', padding: '5px 8px' } }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '7px', bgcolor: disabled ? '#f3f4f6' : '#fafafa',
          '& fieldset': { borderColor: C.cardBorder },
          '&:hover fieldset': { borderColor: disabled ? C.cardBorder : C.accentBorder },
          '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: 1.5 },
        },
      }}
    />
  </Box>
);

const SelectField = ({ label, required, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
    <Typography sx={{ fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}{required && <span style={{ color: C.danger, marginLeft: 2 }}>*</span>}
    </Typography>
    {children}
  </Box>
);

const Section = ({ title, icon, children, color = C.accent }) => (
  <Box sx={{ bgcolor: C.card, borderRadius: '10px', boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, overflow: 'hidden' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: '12px', py: '7px', borderBottom: `1px solid ${C.cardBorder}`, bgcolor: `${color}0d` }}>
      <Box sx={{ color, fontSize: 15, display: 'flex' }}>{icon}</Box>
      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{title}</Typography>
    </Box>
    <Box sx={{ p: '10px 12px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>{children}</Box>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────
const CapitalEdit = () => {
  const { id, mid } = useParams();
  const history = useHistory();
  const { isAuthenticated, companyId, userId, logout, token } = useAuth();

  const [formData, setFormData]       = useState({});
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [saved, setSaved]             = useState(false);
  const [error, setError]             = useState('');

  // Dropdown options
  const [balanceSheetOptions, setBalanceSheetOptions]   = useState([]);
  const [invoiceTypeOptions, setInvoiceTypeOptions]     = useState([]);
  const [selectedBalanceSheet, setSelectedBalanceSheet] = useState(null);
  const [selectedInvoiceType, setSelectedInvoiceType]   = useState(null);

  // Username auto-gen for Staff
  useEffect(() => {
    if (formData.masterType !== 'Staff') return;
    const u = `${(formData.FirstName || '').toLowerCase()}_${(formData.LastName || '').toLowerCase()}`.slice(0, 12);
    setFormData(prev => ({ ...prev, UserName: u }));
  }, [formData.FirstName, formData.LastName]);

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    loadAll();
  }, [id, mid, isAuthenticated, token]);

  const loadAll = async () => {
    setLoading(true);
    try {
      // Load dropdowns + record data in parallel
      const apiMap = {
        '1': ['AccountHeadApi', `/AccountHeadDataDetails/${id}/${mid}`],
        '2': ['AssetsApi',      `/AssetDataDetails/${id}/${mid}`],
        '3': ['BankApi',        `/BankDataDetails/${id}/${mid}`],
        '4': ['CapitalApi',     `/CapitalDataDetails/${id}/${mid}`],
        '5': ['SupplierAndCustomerApi', `/SupplierAndCustomerDataDetails/${id}/${mid}`],
        '6': ['MasterApi',      `/MasterDataDetails/${id}/${mid}`],
        '7': ['StaffApi',       `/StaffDataDetails/${id}/${mid}`],
      };
      const [api, url] = apiMap[mid] || ['StaffApi', `/StaffDataDetails/${id}/${mid}`];

      const [dataRes, bsRes, itRes] = await Promise.all([
        fetchData(api, url, token),
        fetchData('MasterApi', `/BalanceSheetData/${companyId}`, token),
        fetchData('MasterApi', `/InvoiceTypeData/${companyId}`, token),
      ]);

      if (bsRes.responseCode === '1111')
        setBalanceSheetOptions(bsRes.response_data.map(b => ({ value: b.Id, label: b.Name })));
      if (itRes.responseCode === '1111')
        setInvoiceTypeOptions(itRes.response_data.map(i => ({ value: i.Id, label: i.Name })));

      if (dataRes.responseCode === '1111') {
        const d = dataRes.response_data;
        setFormData(d);
        if (d.Balancesheet)  setSelectedBalanceSheet({ value: d.BalanceSheetHeadId, label: d.Balancesheet });
        if (d.invoicetypes)  setSelectedInvoiceType({ value: d.InvoiceTypeId, label: d.invoicetypes });
      } else {
        setError('Failed to load record.');
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error loading data.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (checked ? '1' : '0') : value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData.Name?.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError('');

    const masterBase = {
      CompanyId: companyId,
      Id: formData.MasterId,
      MasterTypeId: formData.MasterTypeId,
      BalanceSheetHeadId: selectedBalanceSheet?.value || formData.BalanceSheetHeadId,
      Name: formData.Name,
      SearchableName: formData.SearchableName,
      Remarks: formData.Remarks,
      OpeningBalance: formData.OpeningBalance,
      IsPayableOrReceivable: formData.IsPayableOrReceivable === '1' || formData.IsPayableOrReceivable === true,
      CreatedBy: formData.CreatedBy,
      CreatedDate: formData.CreatedDate,
      ModifiedBy: userId,
    };

    try {
      switch (formData.masterType) {
        case 'Account Head':
          await updateData('AccountHeadApi', `${id}`, {
            Master: masterBase,
            IncomeExpenseDetails: {
              Id: formData.AccountHeadId, MasterId: formData.MasterId,
              CGSTRate: formData.CGSTRate, SGSTRate: formData.SGSTRate,
              HSNCode: formData.HSNCode, IsExpenseOrIncome: formData.IsExpenseOrIncome,
              UpdatedBy: userId,
            },
          }, token);
          break;
        case 'Assets':
          await updateData('AssetsApi', `${formData.MasterId}`, {
            Master: masterBase,
            AssetsDetails: {
              Id: formData.AssetId, MasterId: formData.MasterId,
              Amount: formData.Amount, Depreciation: formData.Depreciation,
              CGSTRate: formData.CGSTRate, SGSTRate: formData.SGSTRate,
              HSNCode: formData.HSNCode, ModifiedBy: userId,
            },
          }, token);
          break;
        case 'Bank':
          await updateData('BankApi', `${formData.MasterId}`, {
            Master: masterBase,
            BankDetails: {
              Id: formData.BankId, MasterId: formData.MasterId,
              AccountName: formData.AccountName, BranchName: formData.BranchName,
              IFSCCode: formData.IFSCCode, ContactNumber: formData.ContactNumber,
              AddressLine1: formData.AddressLine1, AddressLine2: formData.AddressLine2,
              ModifiedBy: userId,
            },
          }, token);
          break;
        case 'Capital':
          await updateData('CapitalApi', `${id}`, {
            Master: masterBase,
            OwnerDetails: {
              Id: formData.CapitalId, MasterId: formData.MasterId,
              ContactNumber1: formData.ContactNumber1, ContactNumber2: formData.ContactNumber2,
              Email: formData.Email, AddressLine1: formData.AddressLine1,
              AddressLine2: formData.AddressLine2, ModifiedBy: userId,
            },
          }, token);
          break;
        case 'Customer Or Supplier':
          await updateData('SupplierAndCustomerApi', `${formData.MasterId}`, {
            Master: { ...masterBase, MasterGroupId: formData.MastergroupId },
            SupplierAndCustomerDetails: {
              Id: formData.CustomerSupplierId, MasterId: formData.MasterId,
              SalesExecutiveId: null,
              InvoiceTypeId: selectedInvoiceType?.value || formData.InvoiceTypeId,
              ContactNumber1: formData.ContactNumber1, ContactNumber2: formData.ContactNumber2,
              Email: formData.Email, AddressLine1: formData.AddressLine1,
              AddressLine2: formData.AddressLine2,
              DeliveryAddressLine1: formData.DeliveryAddressLine1,
              DeliveryAddressLine2: formData.DeliveryAddressLine2,
              Location: formData.Location, PinCode: formData.PinCode,
              GSTNumber: formData.GSTNumber, PanNumber: formData.PanNumber,
              MRPLCode: formData.MRPLCode, CreditDays: formData.CreditDays,
              CreditLimit: formData.CreditLimit, CustomerType: formData.CustomerType,
              ModifiedBy: userId,
            },
          }, token);
          break;
        case 'Staff':
          await updateData('StaffApi', `${id}`, {
            Master: masterBase,
            StaffDetails: {
              Id: formData.StaffId, MasterId: formData.MasterId,
              ContactNumber1: formData.ContactNumber1, ContactNumber2: formData.ContactNumber2,
              Email: formData.Email, AddressLine1: formData.AddressLine1,
              AddressLine2: formData.AddressLine2, ModifiedBy: userId,
            },
          }, token);
          break;
        default: // Others
          await updateData('MasterApi', `${id}`, {
            ...masterBase, CreatedBy: userId, CreatedDate: new Date(),
          }, token);
      }
      setSaved(true);
      setTimeout(() => { setSaved(false); history.push('/MasterIndex'); }, 1200);
    } catch (err) {
      console.error('Error updating:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const mt = formData.masterType || '';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: C.bg }}>
        <CircularProgress sx={{ color: C.accent }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: C.bg, p: '6px 6px 6px' }}>

      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <AccountBalanceIcon sx={{ fontSize: 16, color: C.accent }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '13px', color: C.text }}>
            {mt} — Edit
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.8 }}>
          <Button component={Link} to="/MasterIndex" size="small"
            startIcon={<ArrowBackIcon sx={{ fontSize: 13 }} />}
            sx={{
              fontSize: '11px', fontWeight: 600, textTransform: 'none',
              color: C.muted, bgcolor: C.card, border: `1px solid ${C.cardBorder}`,
              borderRadius: '7px', px: 1.2, py: 0.4, boxShadow: C.shadow,
              '&:hover': { bgcolor: C.accentDim, borderColor: C.accentBorder, color: C.text },
            }}>
            Back
          </Button>
          <Button onClick={handleSubmit} disabled={saving} size="small"
            startIcon={saving ? <CircularProgress size={11} sx={{ color: '#fff' }} /> : <SaveIcon sx={{ fontSize: 13 }} />}
            sx={{
              fontSize: '11px', fontWeight: 700, textTransform: 'none',
              bgcolor: saved ? '#22c55e' : C.accent, color: '#fff',
              borderRadius: '7px', px: 1.4, py: 0.4, boxShadow: C.shadow,
              '&:hover': { bgcolor: C.accentDark }, transition: 'background 0.3s',
            }}>
            {saved ? 'Saved!' : saving ? 'Saving…' : 'Update'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Box sx={{ mb: 1, px: '10px', py: '6px', bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '7px' }}>
          <Typography sx={{ fontSize: '11px', color: C.danger }}>{error}</Typography>
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>

          {/* ── Common fields ── */}
          <Section title="Basic Information" icon={<LabelIcon />}>
            <Box sx={{ flex: '1 1 180px' }}>
              <SelectField label="Balance Sheet" required>
                <Select
                  value={selectedBalanceSheet}
                  onChange={setSelectedBalanceSheet}
                  options={balanceSheetOptions}
                  placeholder="Select…"
                  isSearchable styles={rsStyles}
                  menuPortalTarget={document.body} menuPosition="fixed"
                />
              </SelectField>
            </Box>
            <Box sx={{ flex: '2 1 200px' }}>
              <Field label="Name" name="Name" value={formData.Name} onChange={handleChange} required />
            </Box>
            <Box sx={{ flex: '2 1 200px' }}>
              <Field label="Searchable Name" name="SearchableName" value={formData.SearchableName} onChange={handleChange} />
            </Box>
            <Box sx={{ flex: '2 1 200px' }}>
              <Field label="Remarks" name="Remarks" value={formData.Remarks} onChange={handleChange} />
            </Box>
            <Box sx={{ flex: '1 1 120px' }}>
              <Field label="Master Type" name="masterType" value={formData.masterType} onChange={handleChange} disabled />
            </Box>
            <Box sx={{ flex: '1 1 120px' }}>
              <Field label="Opening Balance" name="OpeningBalance" value={formData.OpeningBalance} onChange={handleChange} type="number" align="right" />
            </Box>
            <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-end', pb: '2px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="IsPayableOrReceivable"
                    checked={formData.IsPayableOrReceivable === '1' || formData.IsPayableOrReceivable === true}
                    onChange={handleChange} size="small"
                    sx={{ color: C.accent, '&.Mui-checked': { color: C.accent } }}
                  />
                }
                label={<Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'brown' }}>Is Payable</Typography>}
              />
            </Box>
          </Section>

          {/* ── Capital ── */}
          {mt === 'Capital' && (
            <Section title="Contact & Address" icon={<LabelIcon />} color="#22c55e">
              <Box sx={{ flex: '1 1 140px' }}>
                <Field label="Contact No 1" name="ContactNumber1" value={formData.ContactNumber1} onChange={handleChange} maxLength={15} />
              </Box>
              <Box sx={{ flex: '1 1 140px' }}>
                <Field label="Contact No 2" name="ContactNumber2" value={formData.ContactNumber2} onChange={handleChange} maxLength={15} />
              </Box>
              <Box sx={{ flex: '2 1 200px' }}>
                <Field label="Email" name="Email" value={formData.Email} onChange={handleChange} maxLength={40} />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Address Line 1" name="AddressLine1" value={formData.AddressLine1} onChange={handleChange} maxLength={100} />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Address Line 2" name="AddressLine2" value={formData.AddressLine2} onChange={handleChange} maxLength={100} />
              </Box>
            </Section>
          )}

          {/* ── Customer Or Supplier ── */}
          {mt === 'Customer Or Supplier' && (<>
            <Section title="Customer / Supplier Details" icon={<LabelIcon />} color="#3b82f6">
              <Box sx={{ flex: '1 1 160px' }}>
                <SelectField label="Invoice Type">
                  <Select value={selectedInvoiceType} onChange={setSelectedInvoiceType}
                    options={invoiceTypeOptions} placeholder="Select…"
                    isSearchable styles={rsStyles}
                    menuPortalTarget={document.body} menuPosition="fixed" />
                </SelectField>
              </Box>
              <Box sx={{ flex: '1 1 140px' }}>
                <Field label="GST Number" name="GSTNumber" value={formData.GSTNumber} onChange={handleChange} maxLength={15} />
              </Box>
              <Box sx={{ flex: '1 1 140px' }}>
                <Field label="PAN Number" name="PanNumber" value={formData.PanNumber} onChange={handleChange} maxLength={15} />
              </Box>
              <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-end', pb: '2px' }}>
                <RadioGroup row name="CustomerType" value={String(formData.CustomerType ?? '1')} onChange={handleChange}>
                  <FormControlLabel value="1" control={<Radio size="small" sx={{ color: C.accent, '&.Mui-checked': { color: C.accent } }} />}
                    label={<Typography sx={{ fontSize: '11px', color: '#034C53' }}>Customer</Typography>} />
                  <FormControlLabel value="2" control={<Radio size="small" sx={{ color: C.accent, '&.Mui-checked': { color: C.accent } }} />}
                    label={<Typography sx={{ fontSize: '11px', color: '#328E6E' }}>Supplier</Typography>} />
                  <FormControlLabel value="3" control={<Radio size="small" sx={{ color: C.accent, '&.Mui-checked': { color: C.accent } }} />}
                    label={<Typography sx={{ fontSize: '11px', color: '#3A0519' }}>Both</Typography>} />
                </RadioGroup>
              </Box>
            </Section>
            <Section title="Contact & Address" icon={<LabelIcon />} color="#22c55e">
              <Box sx={{ flex: '1 1 140px' }}>
                <Field label="Contact No 1" name="ContactNumber1" value={formData.ContactNumber1} onChange={handleChange} maxLength={15} />
              </Box>
              <Box sx={{ flex: '1 1 140px' }}>
                <Field label="Contact No 2" name="ContactNumber2" value={formData.ContactNumber2} onChange={handleChange} maxLength={15} />
              </Box>
              <Box sx={{ flex: '2 1 200px' }}>
                <Field label="Email" name="Email" value={formData.Email} onChange={handleChange} maxLength={40} />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Address Line 1" name="AddressLine1" value={formData.AddressLine1} onChange={handleChange} maxLength={100} />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Address Line 2" name="AddressLine2" value={formData.AddressLine2} onChange={handleChange} maxLength={100} />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Delivery Address 1" name="DeliveryAddressLine1" value={formData.DeliveryAddressLine1} onChange={handleChange} />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Delivery Address 2" name="DeliveryAddressLine2" value={formData.DeliveryAddressLine2} onChange={handleChange} />
              </Box>
              <Box sx={{ flex: '2 1 160px' }}>
                <Field label="Location" name="Location" value={formData.Location} onChange={handleChange} maxLength={100} />
              </Box>
              <Box sx={{ flex: '1 1 100px' }}>
                <Field label="PIN Code" name="PinCode" value={formData.PinCode} onChange={handleChange} maxLength={20} />
              </Box>
              <Box sx={{ flex: '1 1 100px' }}>
                <Field label="Credit Days" name="CreditDays" value={formData.CreditDays} onChange={handleChange} type="number" align="right" />
              </Box>
              <Box sx={{ flex: '1 1 100px' }}>
                <Field label="Credit Limit" name="CreditLimit" value={formData.CreditLimit} onChange={handleChange} type="number" align="right" />
              </Box>
            </Section>
          </>)}

          {/* ── Bank ── */}
          {mt === 'Bank' && (<>
            <Section title="Bank Details" icon={<LabelIcon />} color="#2563eb">
              <Box sx={{ flex: '1 1 160px' }}>
                <Field label="Account Name" name="AccountName" value={formData.AccountName} onChange={handleChange} />
              </Box>
              <Box sx={{ flex: '1 1 160px' }}>
                <Field label="Branch Name" name="BranchName" value={formData.BranchName} onChange={handleChange} />
              </Box>
              <Box sx={{ flex: '1 1 120px' }}>
                <Field label="IFSC Code" name="IFSCCode" value={formData.IFSCCode} onChange={handleChange} />
              </Box>
              <Box sx={{ flex: '1 1 130px' }}>
                <Field label="Contact No" name="ContactNumber" value={formData.ContactNumber} onChange={handleChange} maxLength={15} />
              </Box>
            </Section>
            <Section title="Address" icon={<LabelIcon />} color="#22c55e">
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Address Line 1" name="AddressLine1" value={formData.AddressLine1} onChange={handleChange} maxLength={100} />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Address Line 2" name="AddressLine2" value={formData.AddressLine2} onChange={handleChange} maxLength={100} />
              </Box>
            </Section>
          </>)}

          {/* ── Staff ── */}
          {mt === 'Staff' && (
            <Section title="Staff Details" icon={<LabelIcon />} color="#f59e0b">
              <Box sx={{ flex: '1 1 140px' }}>
                <Field label="Contact No 1" name="ContactNumber1" value={formData.ContactNumber1} onChange={handleChange} maxLength={15} />
              </Box>
              <Box sx={{ flex: '1 1 140px' }}>
                <Field label="Contact No 2" name="ContactNumber2" value={formData.ContactNumber2} onChange={handleChange} maxLength={15} />
              </Box>
              <Box sx={{ flex: '2 1 200px' }}>
                <Field label="Email" name="Email" value={formData.Email} onChange={handleChange} maxLength={40} />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Address Line 1" name="AddressLine1" value={formData.AddressLine1} onChange={handleChange} maxLength={100} />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Address Line 2" name="AddressLine2" value={formData.AddressLine2} onChange={handleChange} maxLength={100} />
              </Box>
            </Section>
          )}

          {/* ── Account Head ── */}
          {mt === 'Account Head' && (
            <Section title="Account Head Details" icon={<LabelIcon />} color="#8b5cf6">
              <Box sx={{ flex: '1 1 110px' }}>
                <Field label="CGST Rate" name="CGSTRate" value={formData.CGSTRate} onChange={handleChange} type="number" align="right" />
              </Box>
              <Box sx={{ flex: '1 1 110px' }}>
                <Field label="SGST Rate" name="SGSTRate" value={formData.SGSTRate} onChange={handleChange} type="number" align="right" />
              </Box>
              <Box sx={{ flex: '1 1 120px' }}>
                <Field label="HSN Code" name="HSNCode" value={formData.HSNCode} onChange={handleChange} />
              </Box>
              <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-end', pb: '2px' }}>
                <RadioGroup row name="IsExpenseOrIncome" value={String(formData.IsExpenseOrIncome ?? 'true')}
                  onChange={e => setFormData(prev => ({ ...prev, IsExpenseOrIncome: e.target.value === 'true' }))}>
                  <FormControlLabel value="true" control={<Radio size="small" sx={{ color: C.accent, '&.Mui-checked': { color: C.accent } }} />}
                    label={<Typography sx={{ fontSize: '11px', color: '#2563eb' }}>Income</Typography>} />
                  <FormControlLabel value="false" control={<Radio size="small" sx={{ color: C.accent, '&.Mui-checked': { color: C.accent } }} />}
                    label={<Typography sx={{ fontSize: '11px', color: 'brown' }}>Expense</Typography>} />
                </RadioGroup>
              </Box>
            </Section>
          )}

          {/* ── Assets ── */}
          {mt === 'Assets' && (
            <Section title="Asset Details" icon={<LabelIcon />} color="#ef4444">
              <Box sx={{ flex: '1 1 110px' }}>
                <Field label="Amount" name="Amount" value={formData.Amount} onChange={handleChange} type="number" align="right" />
              </Box>
              <Box sx={{ flex: '1 1 110px' }}>
                <Field label="Depreciation" name="Depreciation" value={formData.Depreciation} onChange={handleChange} type="number" align="right" />
              </Box>
              <Box sx={{ flex: '1 1 110px' }}>
                <Field label="CGST Rate" name="CGSTRate" value={formData.CGSTRate} onChange={handleChange} type="number" align="right" />
              </Box>
              <Box sx={{ flex: '1 1 110px' }}>
                <Field label="SGST Rate" name="SGSTRate" value={formData.SGSTRate} onChange={handleChange} type="number" align="right" />
              </Box>
              <Box sx={{ flex: '1 1 120px' }}>
                <Field label="HSN Code" name="HSNCode" value={formData.HSNCode} onChange={handleChange} />
              </Box>
            </Section>
          )}

        </Box>
      </form>
    </Box>
  );
};

export default CapitalEdit;

